/**
 * Artist Salon client cache — Instagram-style stories tray:
 * 1. Show last-known rings immediately (snapshot + TTL cache)
 * 2. Revalidate in background on every visit
 * 3. Warm cover images for visible rings
 */
import { createCacheKey, getCache, setCache, clearCache } from './cache';
import { resolveImageUrl as resolveImageUrlUtil } from './urlResolver';

const STORIES_SNAPSHOT_PREFIX = 'artist-salon:stories-snapshot:';
const MOST_LOVED_SNAPSHOT_PREFIX = 'artist-salon:most-loved-snapshot:';
const STORIES_FETCHED_AT_PREFIX = 'artist-salon:stories-fetched-at:';

const STORIES_TTL_MS = 5 * 60 * 1000;
const MOST_LOVED_TTL_MS = 5 * 60 * 1000;
/** Min gap before another background revalidate in the same tab session */
const REVALIDATE_GAP_MS = 45 * 1000;

function storiesSnapshotKey(uid) {
  return `${STORIES_SNAPSHOT_PREFIX}${uid || 'anon'}`;
}

function mostLovedSnapshotKey(uid) {
  return `${MOST_LOVED_SNAPSHOT_PREFIX}${uid || 'anon'}`;
}

function storiesFetchedAtKey(uid) {
  return `${STORIES_FETCHED_AT_PREFIX}${uid || 'anon'}`;
}

function storiesCacheKey(uid) {
  return createCacheKey('museumStoryGroups', { uid }, uid);
}

function mostLovedCacheKey(uid) {
  return createCacheKey('museumMostLoved', { uid }, uid);
}

/** Firestore timestamps → JSON-safe numbers for localStorage */
function serializeStoryGroups(stories = []) {
  if (!Array.isArray(stories)) return [];
  return stories.map((story) => ({
    ...story,
    peers: (story.peers || []).map((peer) => ({
      ...peer,
      createdAt:
        peer.createdAt?.toMillis?.() ??
        (peer.createdAt?._seconds != null ? peer.createdAt._seconds * 1000 : null) ??
        (typeof peer.createdAt === 'number' ? peer.createdAt : null),
    })),
  }));
}

function readSnapshot(key) {
  try {
    const local = localStorage.getItem(key);
    if (local) {
      const parsed = JSON.parse(local);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch (_) {}
  try {
    const session = sessionStorage.getItem(key);
    if (session) {
      const parsed = JSON.parse(session);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch (_) {}
  return null;
}

function writeSnapshot(key, items = []) {
  if (!Array.isArray(items) || items.length === 0) return;
  try {
    const payload = JSON.stringify(items);
    localStorage.setItem(key, payload);
    sessionStorage.setItem(key, payload);
  } catch (_) {}
}

function readFetchedAt(uid) {
  try {
    const raw = sessionStorage.getItem(storiesFetchedAtKey(uid));
    const n = parseInt(raw || '0', 10);
    return Number.isFinite(n) ? n : 0;
  } catch (_) {
    return 0;
  }
}

function writeFetchedAt(uid) {
  try {
    sessionStorage.setItem(storiesFetchedAtKey(uid), String(Date.now()));
  } catch (_) {}
}

/** Instagram-style: instant tray from disk (snapshot first, then TTL cache). */
export function readCachedSalonStoryGroups(uid) {
  if (!uid) return null;
  const snapshot = readSnapshot(storiesSnapshotKey(uid));
  if (snapshot?.length) return snapshot;
  const cached = getCache(storiesCacheKey(uid));
  if (Array.isArray(cached) && cached.length > 0) return cached;
  return null;
}

export function persistSalonStoryGroups(uid, stories = []) {
  if (!uid || !Array.isArray(stories)) return;
  const safe = serializeStoryGroups(stories);
  writeSnapshot(storiesSnapshotKey(uid), safe);
  setCache(storiesCacheKey(uid), safe, STORIES_TTL_MS);
  writeFetchedAt(uid);
}

export function shouldRevalidateSalonStories(uid) {
  if (!uid) return false;
  const last = readFetchedAt(uid);
  if (!last) return true;
  return Date.now() - last > REVALIDATE_GAP_MS;
}

export function readCachedMostLoved(uid) {
  if (!uid) return null;
  const snapshot = readSnapshot(mostLovedSnapshotKey(uid));
  if (snapshot?.length) return snapshot;
  const cached = getCache(mostLovedCacheKey(uid));
  if (Array.isArray(cached) && cached.length > 0) return cached;
  return null;
}

export function persistMostLoved(uid, items = []) {
  if (!uid || !Array.isArray(items) || items.length === 0) return;
  writeSnapshot(mostLovedSnapshotKey(uid), items);
  setCache(mostLovedCacheKey(uid), items, MOST_LOVED_TTL_MS);
}

/** Preload ring cover URLs (first N) like Instagram warms story thumbnails. */
export function warmSalonStoryCovers(stories = [], limit = 12) {
  if (typeof window === 'undefined' || !Array.isArray(stories)) return;
  const urls = stories
    .slice(0, limit)
    .map((s) => resolveImageUrlUtil(s.coverUrl) || s.coverUrl)
    .filter((u) => typeof u === 'string' && /^https?:\/\//i.test(u));
  urls.forEach((url) => {
    try {
      const img = new Image();
      img.decoding = 'async';
      img.src = url;
    } catch (_) {}
  });
}

export function warmMostLovedThumbnails(items = [], limit = 6) {
  if (typeof window === 'undefined' || !Array.isArray(items)) return;
  items
    .slice(0, limit)
    .map((item) => resolveImageUrlUtil(item.imageUrl) || item.imageUrl)
    .filter((u) => typeof u === 'string' && /^https?:\/\//i.test(u))
    .forEach((url) => {
      try {
        const img = new Image();
        img.decoding = 'async';
        img.src = url;
      } catch (_) {}
    });
}

/** Call after user publishes new public salon content so the tray refreshes. */
export function invalidateSalonCache(uid) {
  if (!uid) return;
  try {
    localStorage.removeItem(storiesSnapshotKey(uid));
    sessionStorage.removeItem(storiesSnapshotKey(uid));
    localStorage.removeItem(mostLovedSnapshotKey(uid));
    sessionStorage.removeItem(mostLovedSnapshotKey(uid));
    sessionStorage.removeItem(storiesFetchedAtKey(uid));
    clearCache(storiesCacheKey(uid));
    clearCache(mostLovedCacheKey(uid));
  } catch (_) {}
}
